/**
 * TextField Component
 * Reusable input field with label, icon, and error support
 */

import { forwardRef, useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

export interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    label?: string;
    error?: string;
    helperText?: string;
    icon?: React.ReactNode;
    endIcon?: React.ReactNode;
    fullWidth?: boolean;
    size?: 'sm' | 'md' | 'lg';
    multiline?: boolean;
    rows?: number;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            label,
            error,
            helperText,
            icon,
            endIcon,
            fullWidth = true,
            size = 'md',
            className = '',
            type = 'text',
            multiline = false,
            rows = 4,
            disabled,
            ...props
        },
        ref
    ) => {
        const [showPassword, setShowPassword] = useState(false);
        // The label always names its field, even when the caller passes no id.
        const generatedId = useId();
        const inputId = props.id ?? generatedId;
        const isPassword = type === 'password';
        const inputType = isPassword && showPassword ? 'text' : type;

        const sizeClasses = {
            sm: 'py-1.5 text-sm',
            md: 'py-2.5 text-base',
            lg: 'py-3 text-lg',
        };

        const baseInputClasses = `
            w-full rounded-lg border transition-colors
            focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
            ${icon ? 'pl-10' : 'pl-4'}
            ${endIcon || isPassword ? 'pr-10' : 'pr-4'}
            ${sizeClasses[size]}
            ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'}
            ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-white'}
        `.trim();

        return (
            <div className={`${fullWidth ? 'w-full' : ''} ${className}`}>
                {label && (
                    <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1.5">
                        {label}
                    </label>
                )}
                <div className="relative">
                    {icon && (
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                            {icon}
                        </div>
                    )}
                    {multiline ? (
                        <textarea
                            className={baseInputClasses}
                            rows={rows}
                            disabled={disabled}
                            value={props.value}
                            onChange={props.onChange as unknown as React.ChangeEventHandler<HTMLTextAreaElement>}
                            onBlur={props.onBlur as unknown as React.FocusEventHandler<HTMLTextAreaElement>}
                            placeholder={props.placeholder}
                            name={props.name}
                            id={inputId}
                            required={props.required}
                            maxLength={props.maxLength}
                        />
                    ) : (
                        <input
                            ref={ref}
                            type={inputType}
                            className={baseInputClasses}
                            disabled={disabled}
                            {...props}
                            id={inputId}
                        />
                    )}
                    {isPassword && (
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    )}
                    {endIcon && !isPassword && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                            {endIcon}
                        </div>
                    )}
                </div>
                {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
                {helperText && !error && (
                    <p className="mt-1 text-sm text-gray-500">{helperText}</p>
                )}
            </div>
        );
    }
);

TextField.displayName = 'TextField';

export default TextField;
